import { Link } from "react-router-dom";
import { PageHero } from "@/components/Sections";
import { ExternalLink, ArrowRight } from "lucide-react";
import biLogo from "@/assets/bronto-intelligence-logo-transparent.png";

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About Bronto Cosmos"
        title="A discovery platform"
        accent="for the universe."
        subtitle="Bronto Cosmos is a space and science discovery platform by Bronto Intelligence Pvt. Ltd., created to make the universe more accessible, visual, and engaging."
      />

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="eyebrow-row"><span className="eyebrow">Mission</span></div>
            <h2 className="display mt-5 text-4xl md:text-5xl">Real data,<br />beautifully presented.</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 text-white/70 leading-relaxed space-y-5 text-base md:text-lg">
            <p>
              Bronto Cosmos brings the work of major space agencies into a unified, modern discovery experience.
              The platform draws on open scientific data — astronomy imagery, planetary mission photographs,
              Earth observation, and near-Earth object measurements — and presents it through a calm, high-fidelity interface.
            </p>
            <p>
              The platform&apos;s design and editorial direction prioritize clarity, accuracy, and a sense of wonder grounded in
              real observation. No exaggeration, no decoration that distracts from the science.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid md:grid-cols-3 gap-5 stagger">
          <Card title="Scientific" body="Built on open scientific data and presented through a calm, high-fidelity interface." />
          <Card title="Visual" body="Designed for reading, browsing, and discovering — calm, dark, and image-led." />
          <Card title="Open" body="Free to use, with no login required. Built so anyone can explore the cosmos." />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="glass-strong p-8 md:p-12">
          <div className="max-w-3xl">
            <div className="eyebrow-row"><span className="eyebrow">Parent company</span></div>
            <img
              src={biLogo}
              alt="Bronto Intelligence"
              className="mt-6 h-8 md:h-10 w-auto"
              data-testid="about-parent-logo"
            />
            <h2 className="display mt-6 text-3xl md:text-4xl lg:text-5xl">Part of <span className="rainbow-text">Bronto Intelligence Pvt. Ltd.</span></h2>
            <p className="mt-5 text-white/70 leading-relaxed text-[15px] md:text-base">
              Bronto Intelligence Private Limited is an Indian technology company building artificial intelligence platforms,
              digital infrastructure, and future-oriented technologies. Bronto Cosmos sits within the Bronto ecosystem alongside
              BrontoAI and MSPConnect, focused specifically on space, science, and public discovery.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://brontointelligence.com" target="_blank" rel="noopener noreferrer" className="btn-pill btn-primary" data-testid="about-parent-cta">
                Visit Bronto Intelligence <ExternalLink size={14} />
              </a>
              <a href="https://brontointelligence.com/leadership" target="_blank" rel="noopener noreferrer" className="btn-pill btn-secondary">
                Meet leadership <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="glass p-8 md:p-10">
          <div className="eyebrow-row"><span className="eyebrow">Leadership</span></div>
          <h2 className="display mt-5 text-3xl md:text-4xl">Leadership and governance.</h2>
          <p className="mt-5 text-white/65 max-w-2xl text-[15px] leading-relaxed">
            The founders, board of directors, and full leadership profiles for the Bronto Intelligence group — including details
            about the chairmanship and non-executive directorship — are published on the parent company website.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="https://brontointelligence.com/leadership" target="_blank" rel="noopener noreferrer" className="btn-pill btn-primary">
              View leadership on Bronto Intelligence <ExternalLink size={14} />
            </a>
            <Link to="/contact" className="btn-pill btn-secondary">Contact us <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Card({ title, body }) {
  return (
    <div className="glass hover-lift p-7">
      <h3 className="display text-2xl">{title}</h3>
      <p className="mt-3 text-sm text-white/60 leading-relaxed">{body}</p>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 items-start">
      <div className="eyebrow col-span-1">{label}</div>
      <div className="col-span-2 text-sm text-white/85">{value}</div>
    </div>
  );
}
